/**
 * UserDriverProfileService
 *
 * Per-user driving licence registry. Owned by CCMS.Api.UserManagement
 * (default-ccms-IdentityDb). Exposes CRUD + the cross-service eligibility
 * endpoint used by BookingManagement's vehicle-booking gate (Phase 5a).
 *
 * Route: api/user-driver-profiles
 */
import { BaseService } from '../base';
export class UserDriverProfileService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UserDriverProfileService.DEFAULT_SERVICE_PATH,
            basePath: UserDriverProfileService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ ====================
    /**
     * Get a user's driver profile.
     * @returns the profile, or null if the user has none.
     */
    async getForUser(userId) {
        const response = await this.getForUserRaw(userId);
        return response.data ?? null;
    }
    getForUserRaw(userId) {
        const url = this.buildUrl(`by-user/${encodeURIComponent(userId)}`);
        return this.http.get(url);
    }
    // ==================== UPSERT ====================
    /**
     * Create or replace the user's profile. UserId on the body is the
     * primary key — re-POSTing for the same UserId replaces the row.
     */
    async upsert(profile) {
        const response = await this.upsertRaw(profile);
        return response.data;
    }
    upsertRaw(profile) {
        const url = this.buildUrl();
        return this.http.post(url, profile);
    }
    // ==================== DEACTIVATE ====================
    /** Soft-revoke a user's profile (sets IsActive = false). */
    async deactivate(userId) {
        const response = await this.deactivateRaw(userId);
        return response.data === true;
    }
    deactivateRaw(userId) {
        const url = this.buildUrl(`by-user/${encodeURIComponent(userId)}/deactivate`);
        return this.http.put(url, {});
    }
    // ==================== ELIGIBILITY (cross-service) ====================
    /**
     * Returns true when the user holds an active, non-expired licence carrying
     * the given <code>licenseClass</code>. Empty <code>licenseClass</code>
     * always returns true. Designed for cross-service callers (BookingManagement
     * vehicle-booking gate, fleet ops, HR duty assignment).
     */
    async isEligible(userId, licenseClass) {
        const response = await this.isEligibleRaw(userId, licenseClass);
        return response.data?.eligible === true;
    }
    isEligibleRaw(userId, licenseClass) {
        const url = this.buildUrl(`by-user/${encodeURIComponent(userId)}/eligibility?licenseClass=${encodeURIComponent(licenseClass)}`);
        return this.http.get(url);
    }
}
UserDriverProfileService.DEFAULT_SERVICE_PATH = 'users';
UserDriverProfileService.BASE_PATH = 'api/user-driver-profiles';
//# sourceMappingURL=UserDriverProfileService.js.map